import React, { memo } from 'react';

import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faCheckCircle,
  faTimesCircle,
  faCircle,
  faMobileScreenButton,
  faBullhorn,
} from '@fortawesome/free-solid-svg-icons';

const STATUS_ICON = {
  pending: { icon: faCircle, className: 'send-progress__icon--pending' },
  sending: { icon: faSpinner, className: 'send-progress__icon--sending', spin: true },
  sent: { icon: faCheckCircle, className: 'send-progress__icon--sent' },
  failed: { icon: faTimesCircle, className: 'send-progress__icon--failed' },
};

const STATUS_LABEL = {
  pending: 'Queued',
  sending: 'Sending…',
  sent: 'Sent',
  failed: 'Failed',
};

const StatusIcon = ({ status }) => {
  const cfg = STATUS_ICON[status] || STATUS_ICON.pending;

  return (
    <FontAwesomeIcon icon={cfg.icon} spin={cfg.spin || false} className={`send-progress__icon ${cfg.className}`} />
  );
};

const StepRow = ({ icon, title, subtitle, status }) => {
  // Normalize to a known status so an unexpected value can't render a blank
  // label or a "…--undefined" class.
  const safeStatus = STATUS_LABEL[status] ? status : 'pending';

  return (
    <li className='send-progress__step'>
      <StatusIcon status={safeStatus} />
      <span className='send-progress__step-label'>
        <span className='send-progress__step-title'>
          {icon && <FontAwesomeIcon icon={icon} className='send-progress__step-kind' />}
          {title}
        </span>
        {subtitle && <span className='send-progress__step-sub'>{subtitle}</span>}
      </span>
      <span className={`send-progress__step-status send-progress__step-status--${safeStatus}`}>
        {STATUS_LABEL[safeStatus]}
      </span>
    </li>
  );
};

/**
 * Live progress modal for the Send Now flow (WWW-braze-update).
 *
 * A push goes out on two channels: the legacy Firebase (app) push and the Braze
 * segment. Each is a labelled row animating sending → sent/failed, then Done.
 *
 * Props
 *   progress: null | {
 *     phase: 'sending' | 'done',
 *     fcm: 'sending' | 'sent' | 'failed',
 *     segments: [{ id, name, status }],   // the resolved Braze campaign(s)
 *   }
 *   onDone: () => void
 */
const SendProgressDialog = ({ progress, onDone }) => {
  if (!progress) return null;

  const { phase, fcm, segments = [] } = progress;
  const isDone = phase === 'done';

  return (
    <Modal show centered backdrop='static' keyboard={false} dialogClassName='send-progress-dialog'>
      <Modal.Header>
        <Modal.Title className='send-progress__title'>
          {isDone ? 'Push complete' : 'Sending push…'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className='send-progress__caption'>Delivering on both channels:</p>
        <ul className='send-progress__steps'>
          {fcm && (
            <StepRow
              icon={faMobileScreenButton}
              title='Firebase (Legacy app)'
              subtitle='App push to your targeting audience'
              status={fcm}
            />
          )}

          {segments.map((seg) => (
            <StepRow key={seg.id} icon={faBullhorn} title='Braze segment' subtitle={seg.name} status={seg.status} />
          ))}
        </ul>
      </Modal.Body>

      {isDone && (
        <Modal.Footer>
          <Button variant='primary' onClick={onDone}>Done</Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default memo(SendProgressDialog);
